---
id: enterprise
title: Enterprise service
owner: Backend Team
status: Active
lastReviewed: 2026-02-02
visibility: internal
tags:
  - enterprise
  - organizations
  - payments
  - slack
  - microservice
---

## Product Overview

### Who it serves

This microservice supports enterprise and white-label workflows for organizations using Rezi, covering enterprise users, organizations, and organization members.

### Primary workflow

Authenticated users and enterprise admins create and manage enterprise users, organizations, organization members, and groups, while enterprise users access billing, subscriptions, orders, resumes, search history, and activity log data.

### Business rules

Most endpoints require Firebase authentication with enterprise user or admin validation, and organization member invitations require active subscriptions unless the invitee is on a @rezi.io email domain.

### Outcomes

Enterprise organizations can manage membership, billing, and access, while admins can review organization data, orders, resumes, and user activity reporting.

### Out of scope

Frontend experiences, non-enterprise consumer workflows, and payment processing details are handled by other services.

### API documentation

[Internal API Documentation](https://wiki.rezi.ai/apis/enterprise-api-docs)

## Technical Overview

### Architecture flow

`apps/micro-services/enterprise/cmd/enterprise/main.go` loads environment configuration, initializes Firebase, and wires Gin routes from `apps/micro-services/enterprise/pkg/api/routes/routes.go` into the shared API server.

### Entry points

- The HTTP service entry point is `apps/micro-services/enterprise/cmd/enterprise/main.go`.
- The OpenAPI generator entry point is `apps/micro-services/enterprise/cmd/spec/main.go`, which builds `spec.json` from the route definitions.
- Controllers live in `apps/micro-services/enterprise/pkg/api/controllers` and handle enterprise users, organizations, payments, orders, resumes, activity logs, and Slack connections.

### Key modules

Service logic lives in `apps/micro-services/enterprise/pkg/services`, with entities in `apps/micro-services/enterprise/pkg/services/entities` and request payloads in `apps/micro-services/enterprise/pkg/api/controllers/requests`. External integrations are wrapped by callers under `apps/micro-services/enterprise/pkg/services/callers` and adaptors under `apps/micro-services/enterprise/pkg/adaptors`.

### Data sources and sinks

The service reads and writes enterprise data through the DB API callers, persists user and RMS data in Firestore via `apps/micro-services/enterprise/pkg/repo/nosql/firestore`, and uploads organization logos to GCS via `apps/micro-services/enterprise/pkg/services/gcsService.go`.

### Integrations and side effects

Integrations include Firebase auth, Slack OAuth and workspace storage, payment service calls for Stripe checkout and billing portals, email delivery for enterprise invitations, resume service PDF download, orders service access, and activity log aggregation for user documents and interviews.

### Constraints and risks

Slack connection state depends on encryption key configuration, and most workflows rely on multiple upstream services (DB API, payment, user, resume) being available and correctly configured.

## Changelog

- 2026-02-03: Can have custom token
- 2026-02-02: Initial documentation.
